import {
  MARKER_ICON_HEIGHT,
  MARKER_ICON_WIDTH,
  RAYBAN_STORES_MARKER_ICON_HEIGHT,
  RAYBAN_STORES_MARKER_ICON_WIDTH,
} from '@/scripts/constants/physical-stores'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import makeDebug from 'debug'
import { EVENTS, STORE_LOCATOR_GOOGLE_CLUSTERING_LABEL } from '../constants/css-classes'
import {
  GENERAL,
  RAYBAN_STORES,
  STORE_LOCATOR_BLACK_CLUSTERING_ICON,
  STORE_LOCATOR_RED_MAP_CLUSTERING_ICON,
} from '../constants/store-locator'

const debug = makeDebug('geocode')

export const initializeGoogleMaps = (mapDiv, config, countryCode, successCallback) => {
  const googleMaps = window?.google?.maps || {}
  const map = new googleMaps.Map(mapDiv, config)
  const geocoder = new googleMaps.Geocoder()
  geocoder.geocode(
    { address: countryCode, componentRestrictions: { country: countryCode } },
    (results, status) => {
      if (status === googleMaps.GeocoderStatus.OK) {
        successCallback(map, results)
      } else {
        debug('Geocode was not successful for the following reason: ' + status)
      }
    }
  )
}

export const initializeGoogleTooltip = () => {
  // eslint-disable-next-line no-undef
  return new google.maps.InfoWindow()
}

export const getGooglePlacesAutocomplete = (input, options) => {
  const googleMaps = window?.google?.maps || {}

  return new googleMaps.places.Autocomplete(input, options)
}

export const mapLatLng = (latitude, longitude) => {
  const googleMaps = window?.google?.maps || {}

  return new googleMaps.LatLng(latitude, longitude)
}

export const mapAddressComponents = (addressComponents) => {
  const streetNumber = addressComponents.find((component) =>
    component.types.includes('street_number')
  )?.long_name
  const route = addressComponents.find((component) => component.types.includes('route'))?.long_name
  const locality = addressComponents.find((component) =>
    component.types.includes('locality')
  )?.long_name
  const postalCode = addressComponents.find((component) =>
    component.types.includes('postal_code')
  )?.long_name
  const postalCodeSuffix = addressComponents.find((component) =>
    component.types.includes('postal_code_suffix')
  )?.long_name
  const state = addressComponents.find((component) =>
    component.types.includes('administrative_area_level_1')
  )?.short_name
  const country = addressComponents.find((component) =>
    component.types.includes('country')
  )?.short_name

  return {
    address1: [streetNumber, route],
    address2: locality,
    zipCode: [postalCode, postalCodeSuffix],
    city: locality,
    state,
    country,
  }
}

export const getAddressFromLatLng = (latLng, countryCode, successCallback) => {
  const googleMaps = window?.google?.maps || {}

  const geocoder = new googleMaps.Geocoder()
  geocoder.geocode({ latLng }, (results, status) => {
    if (status === googleMaps.GeocoderStatus.OK) {
      const resultsFlatMap = results.flatMap((result) => result?.address_components)
      const isUserInCurrentCountry = resultsFlatMap.some(
        (result) => result.short_name === countryCode
      )
      const addressResult = results[1]
      if (!!addressResult && isUserInCurrentCountry) {
        successCallback(addressResult.formatted_address)
      }
    }
  })
}

export const drawMarkersOnGoogleMaps = function (store, storeList, googleMap, zoomLevel) {
  const map = googleMap
  const markersArray = []

  storeList.forEach((item) => {
    const iconSizeWidth =
      item.storeType === RAYBAN_STORES ? RAYBAN_STORES_MARKER_ICON_WIDTH : MARKER_ICON_WIDTH
    const iconSizeHeight =
      item.storeType === RAYBAN_STORES ? RAYBAN_STORES_MARKER_ICON_HEIGHT : MARKER_ICON_HEIGHT
    markersArray.push(
      drawMarkerOnGoogleMaps(item, map, iconSizeWidth, iconSizeHeight, zoomLevel, store)
    )
  })
  return markersArray
}

export const drawMarkerOnGoogleMaps = (
  store,
  map,
  iconSizeWidth,
  iconSizeHeight,
  zoomLevel,
  selectedStore
) => {
  const size = new window.google.maps.Size(iconSizeWidth, iconSizeHeight)
  const marker = new window.google.maps.Marker({
    position: { lat: +store.latitude, lng: +store.longitude },
    map,
    icon: {
      url: store.markerIcon,
      size,
      map,
      scaledSize: size,
    },
    zIndex: store.storeType === RAYBAN_STORES ? 999 : 1,
  })
  marker.setVisible(false)

  if (store.latitude && store.longitude && map) {
    if (selectedStore.latitude === store.latitude && selectedStore.longitude === store.longitude) {
      setStoreCenter(map, store)
    }
    map.setZoom(+zoomLevel)
    marker.setPosition({ lat: +store.latitude, lng: +store.longitude })
    marker.setVisible(true)
  }
  marker.uniqueID = store.uniqueID
  return marker
}

export const setStoreCenter = function (mapValue, storeItem) {
  mapValue && mapValue.setCenter({ lat: +storeItem.latitude, lng: +storeItem.longitude })
}

export const setOpacityOnGoogleMapsMarkers = function (markers, exception) {
  if (markers.length) {
    markers.forEach((item) => {
      const markerLatitude = item.getPosition().lat()
      const markerLongitude = item.getPosition().lng()

      if (markerLatitude === +exception.latitude && markerLongitude === +exception.longitude) {
        item.setOpacity(1)
      } else {
        item.setOpacity(0.5)
      }
    })
  }
}

export const centerGoogleMapUsingCoordinates = (mapInstance, latitude, longitude) => {
  mapInstance.setCenter({ lat: +latitude, lng: +longitude })
}

export const centerGoogleMapUsingLocation = (mapInstance, googleLocationObject) => {
  mapInstance.setCenter(googleLocationObject)
}

export const centerGoogleMapOnCountry = (country, map) => {
  const googleMaps = window?.google?.maps || {}

  const geocoder = new googleMaps.Geocoder()

  geocoder.geocode({ address: country }, function (results, status) {
    if (status == window.google.maps.GeocoderStatus.OK) {
      centerGoogleMapUsingLocation(map, results[0].geometry.location)
      map.setZoom(7)
    }
  })
}

export const clearAllGoogleMapsMarkers = (googleMarkers) => {
  googleMarkers.forEach((marker) => marker.setMap(null))
}

export const clearGoogleMapsEventListeners = (mapInstance) => {
  mapInstance.event.clearListeners(EVENTS.CLICK)
}

const getClusteringRendererObject = (clusteringType = GENERAL, basePath = '') => {
  return {
    render: ({ count, position }) => {
      const clusteringIcon =
        clusteringType === RAYBAN_STORES
          ? `${STORE_LOCATOR_RED_MAP_CLUSTERING_ICON}`
          : `${STORE_LOCATOR_BLACK_CLUSTERING_ICON}`
      // eslint-disable-next-line no-undef
      return new google.maps.Marker({
        position,
        label: {
          className: STORE_LOCATOR_GOOGLE_CLUSTERING_LABEL,
          text: String(count),
          color: 'white',
          fontSize: '13px',
          fontFamily: 'Oswald',
        },
        icon: {
          // TODO: change icon url
          url: `${basePath}${clusteringIcon}`,
        },
        // eslint-disable-next-line no-undef
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
      })
    },
  }
}

const handleClusterClick = (mapReference, tooltipInstance) => {
  setGoogleMapsZoom(mapReference, mapReference.getZoom() - 2)
  tooltipInstance.close()
}

export const initializeGoogleMarkerClustering = (
  mapInstance,
  tooltipInstance,
  markers,
  clusteringType = GENERAL,
  basePath = ''
) => {
  const markerClusterer = new MarkerClusterer({
    map: mapInstance,
    markers,
    renderer: getClusteringRendererObject(clusteringType, basePath),
  })

  const onClusterClick = () => handleClusterClick(mapInstance, tooltipInstance)

  const newClustererClick = (function () {
    const oldClick = markerClusterer.onClusterClick
    return function () {
      const result = oldClick.apply(this, arguments)
      onClusterClick()
      return result
    }
  })()
  markerClusterer.onClusterClick = newClustererClick
  return markerClusterer
}

export const registerOnZoomChangedEvent = (map, callback) => {
  window.google.maps.event.addListener(map, 'zoom_changed', callback)
}

export const triggerEventOnGoogleEntity = (googleEntity, eventType) => {
  // eslint-disable-next-line no-undef
  google.maps.event.trigger(googleEntity, eventType)
}

export const clearGoogleClustering = (googleClusteringInstance) => {
  !!googleClusteringInstance && googleClusteringInstance.clearMarkers()
}

export const setGoogleMapsZoom = (mapInstance, zoomLevel) => {
  mapInstance.setZoom(zoomLevel)
}
